import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Search,
  Delete,
  Person,
  Refresh,
} from '@mui/icons-material';
import { getUsers, deleteUser } from '../services/users';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      setUsers(response.data || response || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.phone?.includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower) ||
      user.status?.toLowerCase().includes(searchLower)
    );
  });

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUser(selectedUser.id);
      setSuccess('User deleted successfully!');
      setDeleteDialogOpen(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete user');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return '#4f46e5';
      case 'user':
        return '#7c3aed';
      default:
        return '#6b7280';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Users Management
            </Typography>
        <Typography variant="body1" sx={{ color: '#6b7280' }}>
          Manage user accounts, roles, and permissions
        </Typography>
      </Box>

      {/* Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(0,0,0,0.02)',
                  '&:hover fieldset': {
                    borderColor: '#52ad83',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchUsers}
                sx={{
                  color: '#6b7280',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 2,
                  '&:hover': { color: '#52ad83', borderColor: '#52ad83' },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Success/Error Messages */}
      {success && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
          }}
        >
          <Typography sx={{ color: '#16a34a' }}>{success}</Typography>
        </Paper>
      )}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          <Typography sx={{ color: '#dc2626' }}>{error}</Typography>
        </Paper>
      )}

      {/* Users Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 6,
            }}
          >
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background: 'rgba(79, 70, 229, 0.05)',
                      '& th': {
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <TableCell>ID</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Test Account</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Person sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
                          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
                            No users found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            {searchTerm
                              ? 'Try adjusting your search criteria'
                              : 'No user accounts have been created yet'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((user, index) => (
                        <TableRow
                          key={user.id}
                          sx={{
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                            '& td': {
                              borderBottom: '1px solid rgba(0,0,0,0.05)',
                              py: 2,
                              color: '#374151',
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              #{user.id || index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  background:
                                    'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Person sx={{ fontSize: 18, color: 'white' }} />
                              </Box>
                              <Typography variant="body2" fontWeight={500}>
                                {user.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{user.phone}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.role}
                              size="small"
                              sx={{
                                background: 'rgba(79, 70, 229, 0.1)',
                                color: getRoleColor(user.role),
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.status}
                              size="small"
                              color={getStatusColor(user.status)}
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                                textTransform: 'capitalize',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.is_test_account ? "Yes" : "No"}
                              size="small"
                              sx={{
                                background: user.is_test_account ? 'rgba(245, 158, 11, 0.1)' : 'rgba(0,0,0,0.05)',
                                color: user.is_test_account ? '#f59e0b' : '#6b7280',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {user.createdAt
                                ? new Date(user.createdAt).toLocaleDateString()
                                : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(user)}
                                sx={{
                                  color: '#6b7280',
                                  '&:hover': { color: '#dc2626' },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                color: '#6b7280',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                '& .MuiTablePagination-select': {
                  color: '#374151',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#6b7280',
                },
                '& .MuiTablePagination-actions button': {
                  color: '#6b7280',
                  '&:hover': {
                    color: '#4f46e5',
                    background: 'rgba(79, 70, 229, 0.08)',
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete User
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#6b7280' }}>
            Are you sure you want to delete <strong>{selectedUser?.name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
